import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AreaId } from 'types/Location';

import { FilterRangeUpdates, Filters, FilterSliderUpdates } from 'types/Search';
import { SearchFiltersUtil } from 'utils/search_filters_util';

import { fetchGroupsAreas } from '../areas/areas';

export type FiltersState = {
  defaultFilters: Filters;
  filters: Filters;
};

export const initialState: FiltersState = {
  defaultFilters: null,
  filters: null
};

type SetDefaultFiltersAction = {
  filters: Filters;
};

type ClearRadioFilterAction = {
  filterKey: string;
};

type FilterRadioToggleAction = {
  filterKey: string;
  filterConfigKey: string;
};

type FilterSetAction = {
  filterKey: string;
  value: any;
};

type FilterRangeAction = {
  filterKey: string;
  value: FilterRangeUpdates | FilterSliderUpdates;
};

type FilterToggleAction = {
  filterKey: string;
  filterConfigKey: string;
};

const clearAllStateFilters = (state: FiltersState) => {
  state.filters = { ...state.defaultFilters };
};

const setStateFilter = (state: FiltersState, action: PayloadAction<FilterSetAction>) => {
  if (!state.filters) {
    return;
  }

  const { filterKey, value } = action.payload;
  SearchFiltersUtil.setFilter(state.filters, filterKey, value);
};

export const filtersSlice = createSlice({
  name: 'filtersState',
  initialState,
  reducers: {
    setDefaultFilters: (state, action: PayloadAction<SetDefaultFiltersAction>) => {
      const { filters } = action.payload;
      state.defaultFilters = { ...filters };
      state.filters = { ...filters };
    },
    clearAllFilters: state => {
      clearAllStateFilters(state);
    },
    clearRadioFilter: (state, action: PayloadAction<ClearRadioFilterAction>) => {
      if (!state.filters) {
        return;
      }

      const { filterKey } = action.payload;
      SearchFiltersUtil.clearRadioFilter(state.filters, filterKey, null);
    },
    toggleRadioFilter: (state, action: PayloadAction<FilterRadioToggleAction>) => {
      if (!state.filters) {
        return;
      }

      const { filterKey, filterConfigKey } = action.payload;
      SearchFiltersUtil.toggleRadioFilter(state.filters, filterKey, filterConfigKey);
    },
    setFilter: (state, action: PayloadAction<FilterSetAction>) => {
      setStateFilter(state, action);
    },
    rangeFilter: (state, action: PayloadAction<FilterRangeAction>) => {
      if (!state.filters) {
        return;
      }

      const { filterKey, value } = action.payload;
      SearchFiltersUtil.sliderFilter(state.filters, filterKey, value);
    },
    toggleFilter: (state, action: PayloadAction<FilterToggleAction>) => {
      if (!state.filters) {
        return;
      }

      const { filterKey, filterConfigKey } = action.payload;
      SearchFiltersUtil.toggleFilter(state.filters, filterKey, filterConfigKey);
    },
    clearAllFiltersAndSetFilter: (state, action) => {
      clearAllStateFilters(state);
      setStateFilter(state, action);
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchGroupsAreas.fulfilled, (state, action) => {
      action.payload.forEach(area => {
        const areaConfig = {
          value: area.ID,
          name: area.name,
          popularity: area.popularity,
          selected: false
        };
        const areaId = area.ID as AreaId;
        if (state.defaultFilters?.areas) {
          state.defaultFilters.areas[areaId] = areaConfig;
        }
        if (state.filters?.areas) {
          const selected = areaId in state.filters.areas ? state.filters.areas[areaId].selected : false;
          state.filters.areas[areaId] = {
            ...areaConfig,
            selected
          };
        }
      });
    });
  }
});

export const {
  setDefaultFilters,
  clearAllFilters,
  clearRadioFilter,
  toggleRadioFilter,
  setFilter,
  rangeFilter,
  toggleFilter,
  clearAllFiltersAndSetFilter
} = filtersSlice.actions;
export default filtersSlice.reducer;
